import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MapContainer from "./components/MapContainer";
import { RestaurantDrawer } from "./components/RestaurantDrawer";
import { RestaurantListDrawer } from "./components/RestaurantListDrawer";
import LoadingSpinner from "./components/LoadingSpinner";
import { useRestaurants } from "../../hooks/useRestaurants";
import { requestUserLocation } from "../../utils/geolocation";
import { Restaurant, UserLocation, MapCenter } from "../../types/restaurant";
import { calculateRestaurantDistances } from "../../utils/firebaseFunctionsDistance";
// import splash from "../src/assets/images/flashpe.png";

/**
 * RestaurantMapViewPage Component
 *
 * Main page component that displays restaurants on an interactive map.
 * Features:
 * - Fetches restaurant data from Firebase on mount
 * - Requests user location permission on mount
 * - Displays restaurants as markers on Google Maps
 * - Shows user's current location if permission granted
 * - Opens drawer with restaurant details when marker is clicked
 * - Handles loading and error states
 */
const RestaurantMapViewPage: React.FC = () => {
  // React Router navigation hook
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeID = searchParams.get("storeID");

  // Fetch restaurants using React Query (cached)
  const {
    data: restaurants = [],
    isLoading,
    error: queryError,
  } = useRestaurants();

  // Check if Google Maps API is loaded
  const [isMapsLoaded, setIsMapsLoaded] = useState(false);

  useEffect(() => {
    // Check if google maps is available
    const checkGoogleMaps = () => {
      if (typeof google !== "undefined" && google.maps) {
        console.log("Google Maps API is loaded and ready");
        setIsMapsLoaded(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkGoogleMaps()) {
      return;
    }

    // If not loaded, check periodically
    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(interval);
      }
    }, 100);

    // Cleanup - wait max 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      console.error("Google Maps API failed to load within 10 seconds");
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Ensure restaurants is always an array
  const restaurantList: Restaurant[] = restaurants || [];

  // State management
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [selectedRestaurant, setSelectedRestaurant] =
    useState<Restaurant | null>(null);
  const [isListDrawerOpen, setIsListDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"takeaway" | "booking">(
    "takeaway"
  );
  const [locationPermission, setLocationPermission] = useState<
    "granted" | "denied" | "prompt"
  >("prompt");
  const [nearbyRestaurantIds, setNearbyRestaurantIds] = useState<string[]>([]);
  const [routeDistances, setRouteDistances] = useState<Map<string, number>>(
    new Map()
  );
  const [isCalculatingDistances, setIsCalculatingDistances] = useState(false);
  const [storesWithDistances, setStoresWithDistances] = useState<Restaurant[]>(
    []
  );

  // Helper to calculate distance between two coordinates in meters (Haversine for quick checks)
  const getDistanceMeters = (loc1: any, loc2: any) => {
    if (!loc1 || !loc2) return Infinity;
    const R = 6371e3; // metres
    const φ1 = (loc1.latitude * Math.PI) / 180;
    const φ2 = (loc2.latitude * Math.PI) / 180;
    const Δφ = ((loc2.latitude - loc1.latitude) * Math.PI) / 180;
    const Δλ = ((loc2.longitude - loc1.longitude) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) *
        Math.cos(φ2) *
        Math.sin(Δλ / 2) *
        Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  // Effect to calculate accurate distances from user location
  useEffect(() => {
    if (
      userLocation &&
      restaurants.length > 0 &&
      nearbyRestaurantIds.length === 0
    ) {
      const calculateDistances = async () => {
        const activeRestaurants = restaurants.filter(
          (r) => r.storeLat != null && r.storeLong != null && r.isHighway === true
        );

        if (activeRestaurants.length === 0) return;

        // --- 1km Threshold Check ---
        const savedLocation = localStorage.getItem("map-last-calculated-location");
        const lastLoc = savedLocation ? JSON.parse(savedLocation) : null;
        
        // Convert UserLocation to format expected by getDistanceMeters if needed
        const currentPos = { latitude: userLocation.latitude, longitude: userLocation.longitude };
        const movedDistance = lastLoc ? getDistanceMeters(currentPos, lastLoc) : Infinity;

        if (movedDistance < 1000 && storesWithDistances.length > 0) {
          console.log(`Skipping map refetch: Moved only ${movedDistance.toFixed(0)}m`);
          return;
        }
        // ----------------------------

        // 1. Calculate Haversine distance first
        const withHaversine = activeRestaurants.map(r => ({
          ...r,
          haversineDist: getDistanceMeters(
            currentPos,
            { latitude: r.storeLat, longitude: r.storeLong }
          ) / 1000
        }));

        // 2. Sort and filter by 300km
        const sortedNearby = withHaversine
          .filter(r => r.haversineDist < 350)
          .sort((a, b) => a.haversineDist - b.haversineDist)
          .slice(0, 15);

        if (sortedNearby.length === 0) return;

        setIsCalculatingDistances(true);
        try {
          const destinations = sortedNearby.map((r) => ({
            lat: r.storeLat,
            lng: r.storeLong,
          }));

          const results = await calculateRestaurantDistances(
            { lat: userLocation.latitude, lng: userLocation.longitude },
            destinations
          );

          // Map results back to restaurants
          const withDistances = sortedNearby.map((restaurant, index) => {
            const result = results[index];
            if (result && result.status === "OK") {
              return {
                ...restaurant,
                distance: result.distance,
                distanceText: result.distanceText,
                durationText: result.durationText,
              };
            }
            return {
              ...restaurant,
              distance: restaurant.haversineDist,
              distanceText: `${restaurant.haversineDist.toFixed(1)} km`
            };
          }).filter(r => r.distance <= 300);

          setStoresWithDistances(withDistances);
          localStorage.setItem("map-last-calculated-location", JSON.stringify(currentPos));
          setIsCalculatingDistances(false);
        } catch (error) {
          console.error("Error calculating distances in MapView:", error);
          setIsCalculatingDistances(false);
          setStoresWithDistances(sortedNearby.map(r => ({
            ...r,
            distance: r.haversineDist,
            distanceText: `${r.haversineDist.toFixed(1)} km`
          })));
        }
      };

      calculateDistances();
    }
  }, [userLocation, restaurants, nearbyRestaurantIds.length, storesWithDistances.length]);



  // Convert query error to string
  const error = queryError ? queryError.message : null;

  // Function to request user location
  const getUserLocation = async () => {
    try {
      const result = await requestUserLocation();

      if (result.location) {
        setUserLocation(result.location);
        setLocationPermission("granted");
      } else {
        setLocationPermission(result.permission);

        // Set Hyderabad as default location when access is denied
        setUserLocation({
          latitude: 17.385044,
          longitude: 78.486671,
        });

        // Log location error for debugging, but don't show to user
        if (result.error) {
          console.info(
            "Location access denied, using Hyderabad as default:",
            result.error
          );
        }
      }
    } catch (err) {
      console.error("Error requesting location:", err);
      setLocationPermission("denied");
      // Set Hyderabad as default location on error
      setUserLocation({
        latitude: 17.385044,
        longitude: 78.486671,
      });
    }
  };

  // Request user location permission on component mount or use Hyderabad as default
  useEffect(() => {
    getUserLocation();
  }, []);

  const [routeTargetRestaurant, setRouteTargetRestaurant] = useState<Restaurant | null>(null);

  // Find restaurant by ID from query param and set it as route target instead of selecting it (which opens drawer)
  useEffect(() => {
    if (restaurants.length > 0 && storeID) {
      const found = restaurants.find((r) => r.dbID === storeID);
      if (found) {
        setRouteTargetRestaurant(found);
      }
    }
  }, [restaurants, storeID]);

  // Calculate default map center based on user location or restaurants
  const mapCenter: MapCenter = useMemo(() => {
    // If a restaurant is selected (e.g., via query param), center on it
    if (selectedRestaurant?.storeLat && selectedRestaurant?.storeLong) {
      return {
        lat: parseFloat(selectedRestaurant.storeLat as any),
        lng: parseFloat(selectedRestaurant.storeLong as any),
      };
    }

    // If user location is available, center on user
    if (userLocation) {
      return {
        lat: userLocation.latitude,
        lng: userLocation.longitude,
      };
    }

    // If restaurants are available, calculate center based on all Active restaurants
    if (restaurantList.length > 0) {
      const validRestaurants = restaurantList.filter(
        (r: Restaurant) =>
          r.storeLat !== 0 && r.storeLong !== 0 && r.isHighway === true
      );

      if (validRestaurants.length > 0) {
        const avgLat =
          validRestaurants.reduce(
            (sum: number, r: Restaurant) => sum + r.storeLat,
            0
          ) / validRestaurants.length;
        const avgLng =
          validRestaurants.reduce(
            (sum: number, r: Restaurant) => sum + r.storeLong,
            0
          ) / validRestaurants.length;

        return {
          lat: avgLat,
          lng: avgLng,
        };
      }
    }

    // Default to Hyderabad if no data available
    return {
      lat: 17.385044,
      lng: 78.486671,
    };
  }, [userLocation, restaurantList]);

  // Calculate appropriate zoom level
  const mapZoom = useMemo(() => {
    // If user location is available, zoom in closer
    if (userLocation) {
      return 13;
    }

    // If restaurants are available, adjust zoom based on spread
    if (restaurantList.length > 0) {
      return 12;
    }

    // Default zoom for country-level view
    return 5;
  }, [userLocation, restaurantList]);

  // Handle restaurant marker click
  const handleMarkerClick = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setSelectedRestaurant(null);
  };

  // Handle list drawer toggle
  const handleListDrawerToggle = () => {
    setIsListDrawerOpen(!isListDrawerOpen);
  };

  // Handle nearby restaurants change from route
  const handleNearbyRestaurantsChange = useCallback(
    (restaurantIds: string[]) => {
      setNearbyRestaurantIds((prevIds) => {
        // Only update if the list actually changed
        const hasChanged =
          prevIds.length !== restaurantIds.length ||
          !prevIds.every((id, index) => id === restaurantIds[index]);

        if (!hasChanged) {
          return prevIds;
        }

        // Automatically open the drawer when restaurants are first found
        if (restaurantIds.length > 0 && prevIds.length === 0) {
          console.log(
            `Opening drawer with ${restaurantIds.length} nearby restaurants`
          );
          setTimeout(() => setIsListDrawerOpen(true), 300);
        } else if (restaurantIds.length === 0 && prevIds.length > 0) {
          // Close drawer when route is cleared
          console.log("Route cleared, closing drawer");
          setIsListDrawerOpen(false);
        }

        return restaurantIds;
      });
    },
    []
  );

  // Filter restaurants based on route selection and Active status
  const displayedRestaurants = React.useMemo(() => {
    // If no route is filtered, use the list that includes user-location distances (if available)
    const baseList =
      nearbyRestaurantIds.length === 0 && storesWithDistances.length > 0
        ? storesWithDistances
        : restaurantList;

    // First filter for Active status and only those on highway
    const activeRestaurants = baseList.filter((r) => r.isHighway === true);

    // Then filter by route if applicable
    if (nearbyRestaurantIds.length > 0) {
      return activeRestaurants.filter((r) =>
        nearbyRestaurantIds.includes(r.dbID)
      );
    }
    return activeRestaurants;
  }, [restaurantList, nearbyRestaurantIds, storesWithDistances]);

  // Handle restaurant selection from list
  const handleRestaurantSelect = (
    restaurant: Restaurant,
    mode: "takeaway" | "booking"
  ) => {
    setSelectedRestaurant(restaurant);
    setDrawerMode(mode);
  };

  // Handle "View Store" action - navigate to restaurant detail page
  const handleViewStore = (restaurantId: string) => {
    // Navigate to restaurant page with restID query parameter
    navigate(`/restaurent?storeID=${restaurantId}`);
  };

  // Handle "Book Table" action - navigate to booking flow
  const handleBookTable = (restaurantId: string) => {
    // For now, navigate to restaurant page
    // TODO: Implement dedicated booking flow
    navigate(`/restaurent?storeID=${restaurantId}`);
  };

  // Show loading spinner while fetching data or loading maps
  if (isLoading || !isMapsLoaded) {
    return (
      <LoadingSpinner
        message={!isMapsLoaded ? "Loading map..." : "Loading restaurants..."}
      />
    );
  }

  // Show error state if data fetch failed
  if (error) {
    return (
      <div
        className="flex items-center justify-center h-screen bg-[#1a1a1a]"
        role="alert"
        aria-live="assertive"
      >
        <div className="text-center p-6 max-w-md">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-red-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            Error Loading Restaurants
          </h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-[#6366f1] hover:bg-[#5558e3] text-white font-semibold rounded-lg transition-colors"
            aria-label="Retry loading restaurants"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state if no restaurants available
  if (!isLoading && restaurantList.length === 0) {
    return (
      <div
        className="flex items-center justify-center h-screen bg-[#1a1a1a]"
        role="status"
        aria-live="polite"
      >
        <div className="text-center p-6 max-w-md">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-gray-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            No Restaurants Found
          </h2>
          <p className="text-gray-400">
            There are currently no restaurants available to display.
          </p>
        </div>
      </div>
    );
  }

  console.log({ restaurants });

  // Main render - map view with drawer
  return (
    <div className=" w-screen h-screen overflow-hidden fixed">
      {/* Map Container */}
      {/* <img src={"flashpe.png"} width={80} height={100} className="mx-auto" /> */}

      <MapContainer
        restaurants={restaurantList}
        userLocation={userLocation}
        selectedRestaurant={selectedRestaurant}
        onMarkerClick={handleMarkerClick}
        center={mapCenter}
        zoom={mapZoom}
        onNearbyRestaurantsChange={handleNearbyRestaurantsChange}
        onRouteDistancesChange={setRouteDistances}
        initialRouteDestination={routeTargetRestaurant}
      />

      {/* Enable Location Button */}
      {locationPermission !== "granted" && (
        <button
          onClick={getUserLocation}
          className="absolute top-20 right-4 z-20 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold px-4 py-2 rounded-lg shadow-lg transition-all flex items-center gap-2"
          aria-label="Enable location access"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          Enable Location
        </button>
      )}

      {/* Floating List Button */}
      <button
        onClick={handleListDrawerToggle}
        className="absolute bottom-8 justify-center bg-[#6366f1] hover:bg-[#5558e3] text-white p-4 rounded-t-2xl w-[calc(100vw-2rem)] shadow-lg transition-colors z-10 flex items-center gap-2 py-3"
        aria-label="View restaurant list"
      >
        {nearbyRestaurantIds.length > 0 ? (
          <>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
            View {displayedRestaurants.length} Restaurants Near Route
          </>
        ) : (
          "View Nearby Restaurants"
        )}
      </button>

      {/* Restaurant List Drawer */}
      <RestaurantListDrawer
        restaurants={displayedRestaurants}
        isOpen={isListDrawerOpen}
        onClose={() => setIsListDrawerOpen(false)}
        onRestaurantSelect={handleRestaurantSelect}
        userLocation={userLocation}
        isRouteFiltered={nearbyRestaurantIds.length > 0}
        routeDistances={routeDistances}
        isCalculatingDistances={isCalculatingDistances}
      />

      {/* Restaurant Details Drawer */}
      <RestaurantDrawer
        restaurant={selectedRestaurant}
        isOpen={selectedRestaurant !== null}
        onClose={handleDrawerClose}
        onViewStore={handleViewStore}
        onBookTable={handleBookTable}
        mode={drawerMode}
      />

      {/* Location Permission Info - Screen reader announcements */}
      {locationPermission === "granted" && (
        <div
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          Location access granted. Map centered on your location.
        </div>
      )}

      {locationPermission === "denied" && (
        <>
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
            <div
              className="bg-[#1a1a1a] border border-gray-800 text-white px-4 py-3 rounded-xl shadow-lg text-sm"
              role="status"
              aria-live="polite"
            >
              <p>Location access denied. Showing all restaurants.</p>
            </div>
          </div>
          <div
            className="sr-only"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            Location access denied. Map showing all available restaurants.
          </div>
        </>
      )}

      {locationPermission === "prompt" && (
        <div
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          Requesting location access to show nearby restaurants.
        </div>
      )}

      {/* Screen reader announcement for selected restaurant */}
      {selectedRestaurant && (
        <div
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {`Selected ${selectedRestaurant.name}. Restaurant details drawer opened.`}
        </div>
      )}
    </div>
  );
};

export default RestaurantMapViewPage;
